<template>
  <KisweForm class="usermedia-deviceselector">
    <p class="warning" v-if="!permissions.video && !permissions.audio">
      Kiswe Studio needs your permission to use your audio &amp; video devices.
    </p>
    <KisweFormGroup :direction="direction" :gap="gap">
      <KisweFormField v-if="needVideo && permissions.video">
        <KisweSelect
          :label="getTranslation('usermedia_camera_label')"
          v-model="selectedVideoDevice"
          :options="videoDevices"
          placeholder="Select"
          :size="size"
          data-qa="select-video-device"
        />
      </KisweFormField>
      <KisweFormField v-if="needAudio && permissions.audio">
        <KisweSelect
          :label="getTranslation('usermedia_microphone_label')"
          v-model="selectedAudioDevice"
          :options="audioDevices"
          placeholder="Select"
          :size="size"
          data-qa="select-audio-device"
        />
      </KisweFormField>
    </KisweFormGroup>
  </KisweForm>
</template>

<script lang="ts">
import './UserMediaDeviceSelector.scss'
import { MediaDevice } from '@/types/usermedia'
import { UserProfile } from '@/types/users'
import useTranslations from '@/compositions/translations'
import { defineComponent, ref } from 'vue'
import { useCurrentTeam } from '@/modules/teams/compositions'
import { KisweForm, KisweFormField, KisweFormGroup, KisweSelect } from 'kiswe-ui'
import { useFlexbox, useSizes } from 'kiswe-ui'
import { KisweSize } from 'kiswe-ui'
import { cloneDeep } from 'lodash'
import { useUsermediaControls } from '../../compositions'

export default defineComponent({
  name: 'UserMediaDeviceSelector',
  components: {
    KisweForm,
    KisweFormField,
    KisweFormGroup,
    KisweSelect
  },
  props: {
    modelValue: {
      type: Object as () => UserProfile,
      required: true
    },
    allowedMediaDevices: {
      required: false,
      type: Array as () => MediaDevice[],
      default: () => Object.values(MediaDevice)
    },
    ...useSizes({ size: KisweSize.Small }).props,
    ...useFlexbox().props
  },
  emits: ['update:modelValue'],
  setup (props) {
    const localValue = ref(cloneDeep(props.modelValue))
    return {
      localValue,
      ...useTranslations(),
      ...useCurrentTeam(),
      ...useUsermediaControls()
    }
  },
  computed: {
    userProfile (): UserProfile {
      return this.localValue
    },
    needVideo (): boolean {
      return this.allowedMediaDevices.includes(MediaDevice.Camera)
    },
    needAudio (): boolean {
      return this.allowedMediaDevices.includes(MediaDevice.Microphone)
    },
    selectedAudioDevice: {
      get (): string|null {
        return this.localValue.selectedAudioDevice ?? null
      },
      set (device: string) {
        this.localValue.selectedAudioDevice = device
        this.$emit('update:modelValue', this.localValue)
      }
    },
    selectedVideoDevice: {
      get (): string|null {
        return this.localValue.selectedVideoDevice ?? null
      },
      set (device: string) {
        this.localValue.selectedVideoDevice = device
        this.$emit('update:modelValue', this.localValue)
      }
    }
  },
  watch: {
    async allowedMediaDevices () {
      await this.$store.direct.dispatch.usermedia.getDevices()
      this.$forceUpdate()
    }
  },
  async mounted () {
    await this.$store.direct.dispatch.usermedia.requestPermissions()
  }
})

</script>
